import React, { useEffect, useRef, useState } from "react";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import CommandBar from "@/components/CommandBar";
import {
    Bot,
    User as UserIcon,
    Loader2,
    ShieldCheck,
    CheckCircle2,
    XCircle,
    Send,
    Sparkles,
    Wrench,
    Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function AIManager() {
    const { currentBusiness } = useApp();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(true);
    const scrollRef = useRef(null);

    // One running conversation per person and business (the command bar
    // writes to the same one), restored when the page opens or the business changes.
    useEffect(() => {
        if (!currentBusiness?.id) return;
        let alive = true;
        sessionStorage.removeItem("ll_last_ai");
        setMessages([]);
        setHistoryLoading(true);
        api.chatHistory(currentBusiness.id)
            .then(({ messages: rows }) => {
                if (!alive) return;
                setMessages(
                    (rows || [])
                        .filter((m) => m.role === "user" || m.role === "assistant")
                        .map((m) => ({
                            role: m.role,
                            content: m.content,
                            action_cards: m.action_cards,
                        })),
                );
            })
            .catch(() => {})
            .finally(() => alive && setHistoryLoading(false));
        return () => {
            alive = false;
        };
    }, [currentBusiness?.id]);

    useEffect(() => {
        if (scrollRef.current)
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, busy]);

    const send = async (text) => {
        if (!text?.trim() || !currentBusiness) return;
        setMessages((m) => [...m, { role: "user", content: text }]);
        setInput("");
        setBusy(true);
        try {
            const { message } = await api.chat(currentBusiness.id, text);
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content: message.content,
                    action_cards: message.action_cards,
                },
            ]);
        } catch {
            toast.error("AI is temporarily unavailable.");
        } finally {
            setBusy(false);
        }
    };

    const presets = [
        "Why did my rating fall?",
        "Reply to today's reviews",
        "What are the biggest complaints?",
        "Generate this week's report",
    ];

    return (
        <div
            className="max-w-5xl mx-auto flex flex-col h-[calc(100vh-6rem)] fade-in"
            data-testid="ai-manager-page"
        >
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="label-eyebrow text-indigo-300">
                        AI Manager · {currentBusiness?.name}
                    </div>
                    <h1 className="mt-1 text-3xl font-extrabold text-slate-50 tracking-tight">
                        Your AI Marketing Manager
                    </h1>
                </div>
                <Badge
                    variant="outline"
                    data-testid="ai-manager-model-pill"
                    className="border-indigo-500/30 bg-indigo-500/10 text-indigo-200 gap-1.5"
                >
                    <Sparkles className="h-3 w-3" /> Grounded in your data
                </Badge>
            </div>

            <div
                ref={scrollRef}
                data-testid="ai-manager-chat-scroll"
                className="surface rounded-2xl flex-1 p-5 overflow-y-auto scroll-invisible space-y-4"
            >
                {historyLoading && messages.length === 0 && (
                    <div className="text-center py-16 text-slate-500" data-testid="ai-history-loading">
                        <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </div>
                )}
                {!historyLoading && messages.length === 0 && (
                    <div className="text-center py-10">
                        <div className="mx-auto h-14 w-14 rounded-2xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
                            <Bot className="h-6 w-6 text-indigo-300" />
                        </div>
                        <h3 className="mt-4 text-lg font-bold text-slate-100">
                            Ask anything about your business.
                        </h3>
                        <p className="mt-1 text-sm text-slate-400 max-w-md mx-auto">
                            The AI can read your reviews, KPIs and complaints, and
                            it can act — drafting replies that route through your
                            approvals.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2 justify-center">
                            {presets.map((p) => (
                                <button
                                    key={p}
                                    onClick={() => send(p)}
                                    data-testid={`ai-preset-${p
                                        .toLowerCase()
                                        .replace(/[^a-z]+/g, "-")
                                        .slice(0, 30)}`}
                                    className="text-xs font-medium text-slate-300 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] px-3 py-1.5 rounded-full border border-white/10"
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {messages.map((m, i) => (
                    <ChatBubble key={i} m={m} />
                ))}
                {busy && (
                    <div className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center shrink-0">
                            <Bot className="h-4 w-4 text-indigo-300" />
                        </div>
                        <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3 text-sm text-slate-400 flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
                            Thinking, running tools…
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-4">
                <div className="surface glow-indigo rounded-2xl backdrop-blur-xl">
                    <div className="flex items-center gap-3 px-3 py-2.5">
                        <div className="hidden sm:flex items-center gap-2 pl-1 pr-2">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-500 opacity-60" />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500" />
                            </span>
                            <span className="label-eyebrow text-indigo-300/90">
                                AI Manager
                            </span>
                        </div>
                        <input
                            data-testid="ai-manager-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    send(input);
                                }
                            }}
                            placeholder="Message the AI Manager…"
                            className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-500 py-2"
                        />
                        <Button
                            data-testid="ai-manager-submit-button"
                            onClick={() => send(input)}
                            disabled={busy || !input.trim() || !currentBusiness}
                            className="rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5"
                        >
                            {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Send className="h-4 w-4" />
                            )}
                            Send
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ChatBubble({ m }) {
    if (m.role === "user") {
        return (
            <div className="flex items-start gap-3 justify-end">
                <div className="rounded-xl bg-indigo-500/15 border border-indigo-500/30 px-4 py-2.5 text-sm text-slate-100 max-w-2xl">
                    {m.content}
                </div>
                <div className="h-8 w-8 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <UserIcon className="h-4 w-4 text-slate-300" />
                </div>
            </div>
        );
    }
    return (
        <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center shrink-0">
                <Bot className="h-4 w-4 text-indigo-300" />
            </div>
            <div className="flex-1 space-y-3 max-w-3xl">
                <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3 text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                    {m.content}
                </div>
                {(m.action_cards || []).map((c, i) => (
                    <ActionCard key={i} card={c} idx={i} />
                ))}
            </div>
        </div>
    );
}

function ActionCard({ card, idx }) {
    if (card.kind === "reply_draft" || card.kind === "reply_published") {
        const isPublished = card.kind === "reply_published";
        return (
            <div
                data-testid={`ai-action-card-${idx}`}
                className="rounded-xl surface p-4"
            >
                <div className="flex items-center gap-2 mb-2">
                    <Wrench className="h-3.5 w-3.5 text-cyan-400" />
                    <span className="label-eyebrow text-cyan-300">
                        Tool · generate_review_reply
                    </span>
                    {isPublished ? (
                        <Badge className="ml-auto bg-emerald-500/15 border border-emerald-500/30 text-emerald-200">
                            Published
                        </Badge>
                    ) : (
                        <Badge className="ml-auto bg-amber-500/15 border border-amber-500/30 text-amber-200">
                            Pending approval
                        </Badge>
                    )}
                </div>
                {card.reviewer && (
                    <div className="text-xs text-slate-400 mb-2 flex items-center gap-2">
                        <Star className="h-3 w-3 text-amber-400" />
                        {card.rating}★ · {card.reviewer}
                    </div>
                )}
                <div className="text-sm text-slate-200 bg-white/[0.02] border border-white/5 rounded-lg p-3 mb-2 whitespace-pre-wrap">
                    {card.reply_text}
                </div>
                {card.reasons?.length > 0 && (
                    <div className="text-[11px] text-rose-300 flex items-center gap-1.5">
                        <ShieldCheck className="h-3 w-3" /> Approval required:{" "}
                        <span className="mono">{card.reasons.join(", ")}</span>
                    </div>
                )}
            </div>
        );
    }
    if (card.kind === "report_generated") {
        return (
            <div
                data-testid={`ai-action-card-${idx}`}
                className="rounded-xl surface p-4"
            >
                <div className="flex items-center gap-2 mb-2">
                    <Wrench className="h-3.5 w-3.5 text-cyan-400" />
                    <span className="label-eyebrow text-cyan-300">
                        Tool · generate_weekly_report
                    </span>
                    <Badge className="ml-auto bg-emerald-500/15 border border-emerald-500/30 text-emerald-200">
                        Generated
                    </Badge>
                </div>
                <div className="text-sm text-slate-300">
                    A fresh weekly report is ready. Open the Reports page to read it.
                </div>
            </div>
        );
    }
    return null;
}
